export type ContinuationSituationAffinity =
  | 'momentum'
  | 'recovery'
  | 'universal';

/**
 * Closed registry of tool jobs. A job is the business outcome a tool
 * produces, independently of how it is built. It powers two governed
 * behaviors:
 * - continuation matching: an action is only routed to a tool whose job
 *   actually serves it (categories alone are too coarse);
 * - subject dedup: a `subjectScoped` job produces a result that stays valid
 *   for the subject for a while, so re-running the same job on the same
 *   subject within the dedup window is redundant.
 */
export type ToolJobKey =
  | 'account_qualification'
  | 'people_discovery'
  | 'meeting_prep'
  | 'followup_email'
  | 'outbound_email'
  | 'call_analysis'
  | 'pipeline_review'
  | 'market_research'
  | 'crm_update'
  | 'content_brief'
  | 'other';

/** Why the next task is worth doing. Deliberately distinct from
 * Activity Spine `work_intent`, which describes the nature of recorded work
 * (`communication`, `diagnostic`, ...), not its commercial purpose. */
export const NEXT_TASK_WORK_INTENTS = [
  'advance_deal',
  'reduce_uncertainty',
  'prepare_interaction',
  'maintain_system_of_record',
  'monitor_risk',
  'coordinate_next_step',
] as const;

export type NextTaskWorkIntentKey = typeof NEXT_TASK_WORK_INTENTS[number];

/** Concrete result promised by the task, independently from its executor. */
export const NEXT_TASK_DESIRED_OUTCOMES = [
  'account_qualification',
  'contact_candidates',
  'meeting_brief',
  'email_draft',
  'personalized_email_draft',
  'account_email_draft',
  'call_analysis',
  'pipeline_diagnostic',
  'research_result',
  'crm_update_proposal',
  'content_brief',
  'business_guidance',
] as const;

export type NextTaskDesiredOutcomeKey = typeof NEXT_TASK_DESIRED_OUTCOMES[number];

export interface NextTaskSemantics {
  workIntent: NextTaskWorkIntentKey;
  jobKey: ToolJobKey;
  desiredOutcome: NextTaskDesiredOutcomeKey;
}

export interface ToolJobDefinition {
  label: string;
  subjectScoped: boolean;
  workIntents: readonly NextTaskWorkIntentKey[];
  desiredOutcomes: readonly NextTaskDesiredOutcomeKey[];
}

export const TOOL_JOB_REGISTRY: Record<ToolJobKey, ToolJobDefinition> = {
  account_qualification: {
    label: 'Qualification de compte',
    subjectScoped: true,
    workIntents: ['reduce_uncertainty'],
    desiredOutcomes: ['account_qualification'],
  },
  people_discovery: {
    label: 'Identification de contacts et décideurs',
    subjectScoped: true,
    workIntents: ['reduce_uncertainty', 'advance_deal'],
    desiredOutcomes: ['contact_candidates', 'research_result'],
  },
  meeting_prep: {
    label: 'Préparation de meeting',
    subjectScoped: false,
    workIntents: ['prepare_interaction', 'advance_deal'],
    desiredOutcomes: ['meeting_brief'],
  },
  followup_email: {
    label: 'Email de suivi',
    subjectScoped: false,
    workIntents: ['advance_deal', 'coordinate_next_step'],
    desiredOutcomes: ['email_draft'],
  },
  outbound_email: {
    label: 'Email de premier contact',
    subjectScoped: false,
    workIntents: ['advance_deal'],
    desiredOutcomes: ['personalized_email_draft', 'account_email_draft'],
  },
  call_analysis: {
    label: 'Analyse d’appel',
    subjectScoped: false,
    workIntents: ['reduce_uncertainty', 'coordinate_next_step'],
    desiredOutcomes: ['call_analysis'],
  },
  pipeline_review: {
    label: 'Revue de pipeline',
    subjectScoped: false,
    workIntents: ['monitor_risk', 'coordinate_next_step'],
    desiredOutcomes: ['pipeline_diagnostic'],
  },
  market_research: {
    label: 'Recherche web ou marché',
    subjectScoped: true,
    workIntents: ['reduce_uncertainty'],
    desiredOutcomes: ['research_result'],
  },
  crm_update: {
    label: 'Mise à jour CRM',
    subjectScoped: false,
    workIntents: ['maintain_system_of_record'],
    desiredOutcomes: ['crm_update_proposal'],
  },
  content_brief: {
    label: 'Brief ou contenu',
    subjectScoped: false,
    workIntents: ['prepare_interaction', 'advance_deal'],
    desiredOutcomes: ['content_brief'],
  },
  other: {
    label: 'Autre',
    subjectScoped: false,
    workIntents: NEXT_TASK_WORK_INTENTS,
    desiredOutcomes: ['business_guidance'],
  },
};

export const TOOL_JOB_KEYS = Object.keys(TOOL_JOB_REGISTRY) as ToolJobKey[];

export const isToolJobKey = (value: unknown): value is ToolJobKey => (
  typeof value === 'string' && TOOL_JOB_KEYS.includes(value as ToolJobKey)
);

export const isNextTaskWorkIntentKey = (
  value: unknown,
): value is NextTaskWorkIntentKey => (
  typeof value === 'string'
  && NEXT_TASK_WORK_INTENTS.includes(value as NextTaskWorkIntentKey)
);

export const isNextTaskDesiredOutcomeKey = (
  value: unknown,
): value is NextTaskDesiredOutcomeKey => (
  typeof value === 'string'
  && NEXT_TASK_DESIRED_OUTCOMES.includes(value as NextTaskDesiredOutcomeKey)
);

export const isValidNextTaskSemantics = (
  value: NextTaskSemantics,
): boolean => {
  const job = TOOL_JOB_REGISTRY[value.jobKey];
  return job.workIntents.includes(value.workIntent)
    && job.desiredOutcomes.includes(value.desiredOutcome);
};

/**
 * Product threshold (not a technical limit): how long a completed run of a
 * subject-scoped job keeps covering that job for the same subject. Within
 * this window, Domino links to the existing result instead of proposing to
 * re-run an equivalent tool.
 */
export const SUBJECT_JOB_DEDUP_WINDOW_HOURS = 14 * 24;

/**
 * Coarse action categories used as a pre-filter before job matching. The
 * final routing decision is semantic (jobKey / completion-time planner);
 * categories only bound the candidate set.
 */
export const CONTINUATION_ACTION_CATEGORIES = [
  'email',
  'meeting',
  'crm_update',
  'research',
  'resource',
  'pipeline',
] as const;

export type ContinuationActionCategory = typeof CONTINUATION_ACTION_CATEGORIES[number];

/**
 * Continuation capability declared by the Forge planner at design time. The
 * planner knows the tool's job and which inputs can be prefilled from a
 * previous run; nothing is inferred from keywords anymore. Affinity and
 * priority stay code-owned.
 */
export interface DraftContinuationCapability {
  jobKey: ToolJobKey;
  label: string;
  description?: string;
  acceptsCategories: ContinuationActionCategory[];
  prefillMapping: Record<string, string>;
}

export interface ToolContinuationCapability {
  label: string;
  description?: string;
  /** Absent on legacy stored capabilities; treated conservatively by dedup. */
  jobKey?: ToolJobKey;
  acceptsCategories: string[];
  situationAffinity: ContinuationSituationAffinity;
  priority: number;
  prefillMapping: Record<string, string>;
}

export interface ToolHandoffOrigin {
  sourceRunId: string;
  sourceActionId: string;
  sourceActionIndex: number;
  sourceActionLabel: string;
  sourceViewType?: string | null;
  /** Exact Domino subject carried across tool boundaries. */
  subjectId?: string | null;
  /** Exact Account root, revalidated server-side before persistence. */
  accountSubjectId?: string | null;
}

/** Exact canonical proposal carried from a persistent Thread to a Tool. It is
 * only a navigation hint on the client; run-flow revalidates every field and
 * the existing acceptance RPC remains the sole mutation authority. */
export interface ThreadProposalHandoff {
  proposalId: string;
  topicId: string;
  expectedTopicVersion: number;
  topicTitle: string;
  /** Null for the work of an app (workspace Thread, no account). */
  subjectId: string | null;
  accountSubjectId: string | null;
}

/** Account context carried by a manual Tool launch before a proposal exists.
 * run-flow revalidates the subject, topic version, decision and objective
 * reference before persisting them with the execution. */
export interface ToolLaunchContext {
  subjectId: string;
  accountSubjectId: string | null;
  topicId: string | null;
  expectedTopicVersion: number | null;
  decisionId: string | null;
  objectiveRef: string | null;
}

/** Exact Forge acceptance hint. The server reauthorizes and fences it. */
export type ForgeWorkOrigin = Pick<ToolHandoffOrigin, 'sourceRunId' | 'sourceActionId'> | { proposalId: string; topicId: string; expectedVersion: number };
